import { allowsNavItem, navPermissions, NAV_ITEMS, type NavItem } from '@/config/nav';

/**
 * Which permission a URL needs, and where an operator who lacks it belongs.
 *
 * The menu already carries one permission code per page and the sidebar hides
 * what an account may not open. A typed URL skips the sidebar, so the router
 * asks the SAME list rather than a second one: a page whose permission changes
 * changes here with it, and a page nobody put in the menu stays open to
 * everyone on purpose (the account profile).
 *
 * This is a UI courtesy, never the enforcement — the gateway refuses the calls
 * regardless.
 */

/** `/tickets` owns `/tickets/new`; `/membersomething` belongs to nobody. */
function covers(navPath: string, pathname: string): boolean {
  if (navPath === '/') return pathname === '/';
  return pathname === navPath || pathname.startsWith(`${navPath}/`);
}

function normalise(pathname: string): string {
  if (pathname === '' || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/**
 * Sub-routes whose gateway policy is STRICTER than the menu entry above them.
 *
 * `/tickets/new` ends in a POST the gateway asks `ticket:create` of, while the
 * menu entry asks only `ticket:read` — so inheriting the menu's code rendered
 * the whole form to a role trimmed down to reading and refused it at the
 * submit, after the operator had typed everything. `/art/themes/new` ends in
 * a POST that needs `art:manage` the same way.
 */
const SUB_ROUTE_PERMISSIONS: Readonly<Record<string, readonly string[]>> = {
  '/tickets/new': ['ticket:create'],
  '/art/themes/new': ['art:manage'],
};

/** The menu entry that owns `pathname`, longest match first, or `undefined`. */
function ownerOf(pathname: string, items: readonly NavItem[]): NavItem | undefined {
  const path = normalise(pathname);
  let owner: NavItem | undefined;
  for (const item of items) {
    if (!covers(item.path, path)) continue;
    if (!owner || item.path.length > owner.path.length) owner = item;
  }
  return owner;
}

/**
 * The permissions the menu entry owning `pathname` declares — ANY of which
 * opens it — or an empty list when nothing owns the URL or the entry is open to
 * every signed-in account.
 *
 * A list rather than one code because a section can be worked with several
 * different permissions and keying it on one of them is wrong in both
 * directions (see `NavItem.anyPermission`).
 */
export function requiredPermissions(
  pathname: string,
  items: readonly NavItem[] = NAV_ITEMS,
): readonly string[] {
  const path = normalise(pathname);
  const stricter = SUB_ROUTE_PERMISSIONS[path];
  if (stricter) return stricter;
  const owner = ownerOf(path, items);
  return owner ? navPermissions(owner) : [];
}

/** Whether this account may open `pathname`, by the same rule the sidebar uses. */
export function canOpenPath(
  pathname: string,
  hasPermission: (code: string) => boolean,
  items: readonly NavItem[] = NAV_ITEMS,
): boolean {
  const codes = requiredPermissions(pathname, items);
  return codes.length === 0 || codes.some(hasPermission);
}

/**
 * The first menu entry this account may open, in the order the sidebar lists
 * them, or `null` when it may open none of them.
 */
export function landingPathFor(
  hasPermission: (code: string) => boolean,
  items: readonly NavItem[] = NAV_ITEMS,
): string | null {
  const allowed = items.find((item) => allowsNavItem(item, hasPermission));
  return allowed?.path ?? null;
}
